import { ClipboardList, FilePlus2, LogOut, Menu, UserRound } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { appConfig } from "../config/appConfig";
import type { PortalView } from "../App";
import type { AuthUser } from "../lib/auth";
import { FeedbackWidget } from "./FeedbackWidget";
import { MarkerIoLoader } from "./MarkerIoLoader";
import { PortalFooter } from "./PortalFooter";

type AppShellProps = {
  activeView: PortalView;
  user: AuthUser;
  isDemo: boolean;
  children: ReactNode;
  onNavigate: (view: PortalView) => void;
  onSignOut: () => void;
};

export function AppShell({ activeView, user, isDemo, children, onNavigate, onSignOut }: AppShellProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const initials = useMemo(() => {
    const first = user.firstName.trim()[0] ?? "";
    const last = user.lastName.trim()[0] ?? "";
    return `${first}${last}`;
  }, [user.firstName, user.lastName]);

  const heroContent = {
    dashboard: {
      eyebrow: "Portal Orders",
      title: "Review prescription activity",
      body: "Search orders, inspect prescription details, and download order PDFs from one focused workspace."
    },
    submit: {
      eyebrow: "New Prescription",
      title: "Submit a prescription",
      body: "Practice, patient, owner, and prescription details are entered in a guided workflow with clear validation."
    },
    account: {
      eyebrow: "Account Settings",
      title: "Manage your prescriber profile",
      body: "Keep contact details, practice locations, DEA information, and signature settings ready for prescription workflows."
    }
  }[activeView];

  function navigate(view: PortalView) {
    onNavigate(view);
    setIsMobileNavOpen(false);
    setIsMenuOpen(false);
  }

  return (
    <div className="portal-shell">
      {appConfig.showTestBanner ? <div className="test-banner">TEST ENVIRONMENT</div> : null}
      <header className="portal-header">
        <a
          className="brand"
          href="/dashboard"
          aria-label="Companion Pharmacy Prescriber Portal"
          onClick={(event) => {
            event.preventDefault();
            navigate("dashboard");
          }}
        >
          <img
            src="https://vp-brand-assets.s3.amazonaws.com/companion-pharmacy/companion_pharmacy_logo_primary.png"
            alt="Companion Pharmacy"
          />
          <span>Prescriber Portal</span>
        </a>

        <nav className="desktop-nav" aria-label="Primary">
          <button type="button" aria-current={activeView === "dashboard" ? "page" : undefined} onClick={() => navigate("dashboard")}>
            <ClipboardList size={17} />
            Dashboard
          </button>
          <button type="button" aria-current={activeView === "submit" ? "page" : undefined} onClick={() => navigate("submit")}>
            <FilePlus2 size={17} />
            Submit Prescription
          </button>
          <button type="button" aria-current={activeView === "account" ? "page" : undefined} onClick={() => navigate("account")}>
            <UserRound size={17} />
            Account
          </button>
        </nav>

        <div className="header-actions">
          {isDemo ? <span className="demo-pill">Demo data</span> : null}
          <button
            className="icon-button mobile-nav-button"
            type="button"
            aria-label="Open navigation"
            aria-expanded={isMobileNavOpen}
            onClick={() => setIsMobileNavOpen((value) => !value)}
          >
            <Menu size={20} />
          </button>
          <div className="avatar-menu">
            <button
              className="avatar-button"
              type="button"
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
              aria-label="Open account menu"
              onClick={() => setIsMenuOpen((value) => !value)}
              onBlur={(event) => {
                if (!event.currentTarget.parentElement?.contains(event.relatedTarget as Node | null)) {
                  setIsMenuOpen(false);
                }
              }}
            >
              {initials}
            </button>
            {isMenuOpen ? (
              <div className="account-menu" role="menu">
                <button role="menuitem" type="button" onClick={onSignOut}>
                  <LogOut size={16} />
                  Log out
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </header>
      {isMobileNavOpen ? (
        <nav className="mobile-nav" aria-label="Primary">
          <button type="button" aria-current={activeView === "dashboard" ? "page" : undefined} onClick={() => navigate("dashboard")}>
            <ClipboardList size={17} />
            Dashboard
          </button>
          <button type="button" aria-current={activeView === "submit" ? "page" : undefined} onClick={() => navigate("submit")}>
            <FilePlus2 size={17} />
            Submit Prescription
          </button>
          <button type="button" aria-current={activeView === "account" ? "page" : undefined} onClick={() => navigate("account")}>
            <UserRound size={17} />
            Account
          </button>
        </nav>
      ) : null}

      <main className="portal-main">
        <section
          className={`portal-hero ${activeView === "submit" || activeView === "dashboard" ? "portal-hero-wide" : ""}`}
          aria-labelledby="account-title"
        >
          <div>
            <p className="eyebrow">{heroContent.eyebrow}</p>
            <h1 id="account-title">{heroContent.title}</h1>
            <p>{heroContent.body}</p>
          </div>
        </section>
        {children}
      </main>
      <PortalFooter />
      <FeedbackWidget />
      <MarkerIoLoader user={user} />
    </div>
  );
}
